import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";

import { cn } from "@perceptron/utils/cn";
import type Konva from "konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { Stage, Layer, Image, Rect } from "react-konva";
import useImage from "use-image";

import { KONVA_SHAPES, KONVA_LAYERS, KONVA_GROUPS } from "../constants";
import { MarkupToolbar } from "../toolbar/MarkupToolbar";
import { MarkupMode, DrawingState } from "../types";

import { AnnotationList } from "./AnnotationList";
import { BoxAnnotation } from "./BoxAnnotation";
import { PointAnnotation } from "./PointAnnotation";
import { AnnotationTransformer } from "./AnnotationTransformer";
import { RenameLabelSheet } from "./RenameLabelSheet";
import { useAnnotationKeyboard } from "../hooks/useAnnotationKeyboard";
import { useCanvasDimensions } from "../hooks/useCanvasDimensions";
import { useTransformer } from "../hooks/useTransformer";
import { useAnnotationEditorStore } from "../store/annotationEditorStore";
import { useAnnotationStore } from "../store/annotationStore";
import { generateMediaKey } from "../utils/annotationUtils";
import { EditorAnnotation, EditorAnnotationType } from "../store/annotationTypes";
import { BoxAnnotationStrategy, PointAnnotationStrategy } from "../strategies";
import {
  screenToNormalized,
  normalizedToScreen,
  calculateBoxDragBounds,
  calculatePointDragBounds,
  NORMALIZED_BOUNDS,
} from "../utils/coordinates";

export interface AnnotationCanvasProps {
  imageUrl?: string;
  isAnnotating: boolean;
  onToggleAnnotating: () => void;
  mediaSetId?: string;
  mediaKey?: string;
}

// Stable empty array reference to avoid infinite loops
const EMPTY_ANNOTATIONS: EditorAnnotation[] = [];

const AnnotationCanvas: React.FC<AnnotationCanvasProps> = ({
  imageUrl,
  isAnnotating,
  onToggleAnnotating,
  mediaSetId,
  mediaKey,
}) => {
  const { container, setContainerDimensions, setAspectRatio } = useAnnotationEditorStore();
  const padding = 50;
  const TOOLBAR_HEIGHT = 70; // Height of the MarkupToolbar in pixels
  const TOP_CONTROLS_HEIGHT = 50; // Height reserved for close button and top controls
  const containerRef = useRef<HTMLDivElement>(null);
  const [image, status] = useImage(imageUrl ?? "");
  const imageLoaded = status === "loaded";
  const stageRef = useRef<Konva.Stage>(null);

  // EditorAnnotation state - use the new annotationStore with media support
  const annotations = useAnnotationStore((state) => {
    if (!mediaSetId || !mediaKey) {
      return EMPTY_ANNOTATIONS;
    }
    const compositeKey = `${mediaSetId}:${mediaKey}`;
    return state.annotationsByMedia.get(compositeKey) || EMPTY_ANNOTATIONS;
  });
  const addAnnotation = useAnnotationStore((state) => state.addAnnotation);
  const updateAnnotation = useAnnotationStore((state) => state.updateAnnotation);
  const deleteAnnotation = useAnnotationStore((state) => state.deleteAnnotation);
  const undo = useAnnotationStore((state) => state.undo);
  // Don't subscribe to hoveredAnnotationId here - let child components handle it
  const setHoveredAnnotationId = useAnnotationStore((state) => state.setHoveredAnnotationId);
  const canUndo = useAnnotationStore((state) => {
    if (!state.activeMediaSetId || !state.activeMediaKey) return false;
    const mediaKey = generateMediaKey(state.activeMediaSetId, state.activeMediaKey);
    const mediaHistory = state.history.get(mediaKey);
    return mediaHistory ? mediaHistory.past.length > 0 : false;
  });

  /**
   * Hover event handlers for annotations.
   * Memoized to prevent unnecessary re-renders of child components.
   */
  const handleAnnotationMouseEnter = useCallback(
    (annotationId: string) => {
      setHoveredAnnotationId(annotationId);
    },
    [setHoveredAnnotationId],
  );

  const handleAnnotationMouseLeave = useCallback(() => {
    setHoveredAnnotationId(null);
  }, [setHoveredAnnotationId]);

  // Drawing state
  const [markupMode, setMarkupMode] = useState<MarkupMode>(MarkupMode.Box);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawingState, setDrawingState] = useState<DrawingState>({
    isDrawing: false,
    startPoint: null,
    tempBox: null,
  });

  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);

  // Clear selection if the selected annotation no longer exists
  useEffect(() => {
    if (selectedId && !annotations.some((ann) => ann.id === selectedId)) {
      setSelectedId(null);
    }
  }, [selectedId, annotations]);

  /**
   * Canvas layer references for direct DOM manipulation
   */
  const layerRef = useRef<Konva.Layer>(null);
  const drawingLayerRef = useRef<Konva.Layer>(null);
  const tempRectRef = useRef<Konva.Rect>(null);

  /**
   * Drawing state reference for performance optimization.
   * Allows access to current state without triggering re-renders during drag operations.
   */
  const drawingStateRef = useRef<DrawingState>(drawingState);

  // Calculate aspect ratio - use default if image not loaded yet
  const aspectRatio = image ? image.width / image.height : 16 / 9;

  // Use custom hooks - subtract toolbar and top controls height from container height for image positioning
  const {
    rectWidth,
    rectHeight,
    rectX,
    rectY: calculatedRectY,
  } = useCanvasDimensions(
    container.width,
    container.height - TOOLBAR_HEIGHT - TOP_CONTROLS_HEIGHT,
    aspectRatio,
    padding,
  );

  // Adjust rectY to account for the top controls area
  const rectY = calculatedRectY + TOP_CONTROLS_HEIGHT;

  const transformerRef = useTransformer(layerRef, selectedId, isAnnotating, markupMode);

  // Delete handler that also clears selection
  const handleDeleteAnnotation = useCallback(
    (id: string) => {
      deleteAnnotation(id);
      setSelectedId(null);
    },
    [deleteAnnotation],
  );

  const selectedAnnotation = useMemo(
    () => (selectedId ? annotations.find((a) => a.id === selectedId) : null),
    [selectedId, annotations],
  );

  const handleRenameClick = useCallback(() => {
    if (selectedId) {
      setRenameDialogOpen(true);
    }
  }, [selectedId]);

  const handleRenameSubmit = useCallback(
    (newLabel: string) => {
      if (selectedId && selectedAnnotation) {
        updateAnnotation(selectedId, { ...selectedAnnotation, label: newLabel });
        setRenameDialogOpen(false);
      }
    },
    [selectedId, selectedAnnotation, updateAnnotation],
  );

  useAnnotationKeyboard(isAnnotating, canUndo, undo, selectedId, handleDeleteAnnotation, renameDialogOpen);

  useEffect(() => {
    // Update aspect ratio in store when it changes
    setAspectRatio(aspectRatio);
  }, [setAspectRatio, aspectRatio]);

  useEffect(() => {
    // Set up ResizeObserver to track container dimensions
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        // Use full container height for Stage to capture all pointer events
        setContainerDimensions(width, height);
      }
    });

    const currentContainer = containerRef.current;
    if (currentContainer) {
      resizeObserver.observe(currentContainer);
      // Get initial dimensions
      const rect = currentContainer.getBoundingClientRect();
      // Use full container height for Stage to capture all pointer events
      setContainerDimensions(rect.width, rect.height);
    }

    return () => {
      if (currentContainer) {
        resizeObserver.unobserve(currentContainer);
      }
      resizeObserver.disconnect();
    };
  }, [setContainerDimensions]);

  // Coordinate conversion functions with memoization
  const screenToNormalizedMemo = useCallback(
    (screenX: number, screenY: number) => screenToNormalized(screenX, screenY, rectX, rectY, rectWidth, rectHeight),
    [rectX, rectY, rectWidth, rectHeight],
  );

  const normalizedToScreenMemo = useCallback(
    (normX: number, normY: number) => normalizedToScreen(normX, normY, rectX, rectY, rectWidth, rectHeight),
    [rectX, rectY, rectWidth, rectHeight],
  );

  /**
   * Synchronize drawing state reference with React state.
   * Ensures strategies can access current state without causing re-renders.
   */
  useEffect(() => {
    drawingStateRef.current = drawingState;
  }, [drawingState]);

  /**
   * Optimized drawing state setter using direct DOM manipulation.
   *
   * This implementation bypasses React's reconciliation process during drag operations
   * by directly manipulating Konva DOM attributes. React state is only updated when
   * drawing begins or ends, significantly improving performance during interactive
   * annotation creation.
   *
   * @param newState - New drawing state or state updater function
   */
  const setDrawingStateOptimized = useCallback(
    (newState: DrawingState | ((prev: DrawingState) => DrawingState)) => {
      const nextState = typeof newState === "function" ? newState(drawingStateRef.current) : newState;

      // Update temporary rectangle via direct DOM manipulation for smooth interactions
      if (tempRectRef.current && nextState.tempBox) {
        const { tempBox } = nextState;
        tempRectRef.current.setAttrs({
          x: normalizedToScreenMemo(tempBox.x, 0).x,
          y: normalizedToScreenMemo(0, tempBox.y).y,
          width: (tempBox.width / NORMALIZED_BOUNDS.MAX) * rectWidth,
          height: (tempBox.height / NORMALIZED_BOUNDS.MAX) * rectHeight,
          visible: true,
        });
        drawingLayerRef.current?.batchDraw();
      } else if (tempRectRef.current && !nextState.tempBox) {
        tempRectRef.current.visible(false);
        drawingLayerRef.current?.batchDraw();
      }

      const wasDrawing = drawingStateRef.current.isDrawing;

      // Always maintain current reference for strategy access
      drawingStateRef.current = nextState;

      // Update React state only on drawing state transitions to minimize re-renders
      if (nextState.isDrawing !== wasDrawing) {
        setDrawingState(nextState);
      }
    },
    [normalizedToScreenMemo, rectWidth, rectHeight],
  );

  // Create strategy based on markup mode
  const strategy = useMemo(() => {
    const strategyProps = {
      screenToNormalized: screenToNormalizedMemo,
      normalizedToScreen: normalizedToScreenMemo,
      annotations,
      addAnnotation,
      drawingState,
      drawingStateRef,
      setDrawingState: setDrawingStateOptimized,
      rectWidth,
      rectHeight,
    };

    switch (markupMode) {
      case MarkupMode.Box:
        return new BoxAnnotationStrategy(strategyProps);
      case MarkupMode.Point:
        return new PointAnnotationStrategy(strategyProps);
      default:
        return null;
    }
  }, [
    markupMode,
    screenToNormalizedMemo,
    normalizedToScreenMemo,
    annotations,
    addAnnotation,
    drawingState,
    setDrawingStateOptimized,
    rectWidth,
    rectHeight,
  ]);

  /**
   * Pointer event handlers for annotation interactions
   */
  const handlePointerDown = useCallback(
    (e: KonvaEventObject<PointerEvent>) => {
      // Check if we clicked on empty space (the stage or image)
      const clickedOnEmpty = e.target === e.target.getStage() || e.target.name() === KONVA_SHAPES.IMAGE;

      // Helper function to find the annotation group in the hierarchy
      const findAnnotationGroup = (node: Konva.Node): Konva.Group | null => {
        // Check if current node is an annotation group
        if (
          node.hasName &&
          (node.hasName(KONVA_GROUPS.BOX_ANNOTATION) || node.hasName(KONVA_GROUPS.POINT_ANNOTATION))
        ) {
          return node as Konva.Group;
        }
        // Check parent
        const parent = node.getParent();
        if (parent && parent.nodeType !== "Stage" && parent.nodeType !== "Layer") {
          return findAnnotationGroup(parent);
        }
        return null;
      };

      // Check if we clicked on any part of an annotation
      const annotationGroup = findAnnotationGroup(e.target);

      if (annotationGroup && isAnnotating) {
        // Switch to selection mode when tapping on an annotation
        setMarkupMode(MarkupMode.Selection);

        // Get annotation ID from the rect/circle child within the group
        const shape = annotationGroup.findOne(
          (node: Konva.Node) =>
            node.hasName && (node.hasName(KONVA_SHAPES.BOX_RECT) || node.hasName(KONVA_SHAPES.POINT_MARKER)),
        );

        if (shape) {
          const annotationId = shape.id();
          if (annotationId) {
            setSelectedId(annotationId);
          }
        }
        return;
      }

      if (clickedOnEmpty && markupMode === MarkupMode.Selection) {
        setSelectedId(null);
        return;
      }

      if (!isAnnotating || !strategy) return;
      strategy.handlePointerDown(e);
    },
    [isAnnotating, strategy, markupMode, setMarkupMode],
  );

  /**
   * Handle pointer leave event - completes drawing operation when cursor exits the stage
   */
  const handlePointerLeave = useCallback(() => {
    if (drawingState.isDrawing && strategy) {
      strategy.handlePointerUp({} as KonvaEventObject<PointerEvent>);
    }
  }, [drawingState.isDrawing, strategy]);

  const handlePointerMove = useCallback(
    (e: KonvaEventObject<PointerEvent>) => {
      if (!strategy) return;
      strategy.handlePointerMove(e);
    },
    [strategy],
  );

  const handlePointerUp = useCallback(
    (e: KonvaEventObject<PointerEvent>) => {
      if (!strategy) return;
      strategy.handlePointerUp(e);
    },
    [strategy],
  );

  /**
   * EditorAnnotation transformation event handlers
   */
  const handleBoxTransform = useCallback(
    (annotation: EditorAnnotation) => (e: KonvaEventObject<Event>) => {
      const node = e.target;
      const group = node.getParent();
      if (!group) return;

      const label = group.findOne(`#label-${annotation.id}`);
      if (label) {
        const x = node.x();
        const y = node.y();
        const width = node.width() * node.scaleX();
        label.position({
          x: x + width / 2,
          y: y - 8,
        });
      }
    },
    [],
  );

  const handleBoxTransformEnd = useCallback(
    (annotation: EditorAnnotation) => (e: KonvaEventObject<Event>) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      // Reset scale
      node.scaleX(1);
      node.scaleY(1);

      const newWidth = Math.max(10, node.width() * scaleX);
      const newHeight = Math.max(10, node.height() * scaleY);
      const newPos = screenToNormalizedMemo(node.x(), node.y());

      // Include all properties for proper undo/redo functionality
      updateAnnotation(annotation.id, {
        ...annotation,
        x: newPos.x,
        y: newPos.y,
        width: (newWidth / rectWidth) * NORMALIZED_BOUNDS.MAX,
        height: (newHeight / rectHeight) * NORMALIZED_BOUNDS.MAX,
      });
    },
    [screenToNormalizedMemo, updateAnnotation, rectWidth, rectHeight],
  );

  const handleAnnotationDragEnd = useCallback(
    (annotation: EditorAnnotation, screenPos: { x: number; y: number }) => (e: KonvaEventObject<DragEvent>) => {
      const node = e.target;
      const newPos = screenToNormalizedMemo(node.x() + screenPos.x, node.y() + screenPos.y);

      updateAnnotation(annotation.id, {
        ...annotation,
        x: newPos.x,
        y: newPos.y,
      });

      // Reset group position after state update
      node.position({ x: 0, y: 0 });
    },
    [screenToNormalizedMemo, updateAnnotation],
  );

  const canvasContent = container.width > 0 && container.height > 0 && (
    <>
      <Stage
        height={container.height}
        ref={stageRef}
        width={container.width}
        onPointerDown={handlePointerDown}
        onPointerLeave={handlePointerLeave}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Image Layer */}
        <Layer name={KONVA_LAYERS.IMAGE}>
          {image && (
            <Image
              height={rectHeight}
              image={image}
              name={KONVA_SHAPES.IMAGE}
              perfectDrawEnabled={false}
              shadowBlur={10}
              shadowColor="black"
              shadowOffsetX={0}
              shadowOffsetY={2}
              shadowOpacity={0.2}
              width={rectWidth}
              x={rectX}
              y={rectY}
              onMouseEnter={(e) => {
                // Show crosshair cursor when in Box or Point markup mode
                if (isAnnotating && (markupMode === MarkupMode.Box || markupMode === MarkupMode.Point)) {
                  const stage = e.target.getStage();
                  if (stage) {
                    stage.container().style.cursor = "crosshair";
                  }
                }
              }}
              onMouseLeave={(e) => {
                // Reset cursor when leaving the image
                const stage = e.target.getStage();
                if (stage) {
                  stage.container().style.cursor = "default";
                }
              }}
            />
          )}
        </Layer>

        {/* Annotations Layer */}
        <Layer name={KONVA_LAYERS.ANNOTATIONS} ref={layerRef}>
          {imageLoaded &&
            annotations.map((annotation) => {
              const screenPos = normalizedToScreenMemo(annotation.x, annotation.y);

              if (annotation.type === EditorAnnotationType.Box) {
                const screenWidth = ((annotation.width ?? 0) / NORMALIZED_BOUNDS.MAX) * rectWidth;
                const screenHeight = ((annotation.height ?? 0) / NORMALIZED_BOUNDS.MAX) * rectHeight;

                const dragBoundFunc = (pos: { x: number; y: number }) =>
                  calculateBoxDragBounds(
                    pos,
                    screenPos,
                    screenWidth,
                    screenHeight,
                    rectX,
                    rectY,
                    rectWidth,
                    rectHeight,
                  );

                return (
                  <BoxAnnotation
                    annotation={annotation}
                    dragBoundFunc={dragBoundFunc}
                    isAnnotating={isAnnotating}
                    isSelected={selectedId === annotation.id}
                    isSelectionMode={markupMode === MarkupMode.Selection}
                    key={annotation.id}
                    screenHeight={screenHeight}
                    screenPos={screenPos}
                    screenWidth={screenWidth}
                    onDragEnd={handleAnnotationDragEnd(annotation, screenPos)}
                    onMouseEnter={() => handleAnnotationMouseEnter(annotation.id)}
                    onMouseLeave={handleAnnotationMouseLeave}
                    onSelect={() => {
                      if (isAnnotating && markupMode === MarkupMode.Selection) {
                        setSelectedId(annotation.id);
                      }
                    }}
                    onTransform={handleBoxTransform(annotation)}
                    onTransformEnd={handleBoxTransformEnd(annotation)}
                  />
                );
              } else if (annotation.type === EditorAnnotationType.Point) {
                const dragBoundFunc = (pos: { x: number; y: number }) =>
                  calculatePointDragBounds(pos, screenPos, rectX, rectY, rectWidth, rectHeight);

                return (
                  <PointAnnotation
                    annotation={annotation}
                    dragBoundFunc={dragBoundFunc}
                    isAnnotating={isAnnotating}
                    isSelected={selectedId === annotation.id}
                    isSelectionMode={markupMode === MarkupMode.Selection}
                    key={annotation.id}
                    screenPos={screenPos}
                    onDragEnd={handleAnnotationDragEnd(annotation, screenPos)}
                    onMouseEnter={() => handleAnnotationMouseEnter(annotation.id)}
                    onMouseLeave={handleAnnotationMouseLeave}
                    onSelect={() => {
                      if (isAnnotating && markupMode === MarkupMode.Selection) {
                        setSelectedId(annotation.id);
                      }
                    }}
                  />
                );
              }

              return null;
            })}

          {/* Transformer for selected box */}
          <AnnotationTransformer
            isAnnotating={isAnnotating}
            markupMode={markupMode}
            rectHeight={rectHeight}
            rectWidth={rectWidth}
            rectX={rectX}
            rectY={rectY}
            ref={transformerRef}
            selectedId={selectedId}
          />
        </Layer>

        {/* 
          Drawing Layer
          
          Dedicated layer for rendering temporary drawing preview.
          During bounding box creation, we update this rect's attributes directly
          using Konva's setAttrs() method instead of updating React state.
          This avoids re-rendering the entire component tree on every mouse move.
        */}
        <Layer name={KONVA_LAYERS.DRAWING} ref={drawingLayerRef}>
          <Rect
            dash={[5, 5]}
            fill="rgba(245, 131, 16, 0.1)"
            listening={false}
            perfectDrawEnabled={false}
            ref={tempRectRef}
            stroke="#F27D28"
            strokeWidth={2}
            visible={false}
          />
        </Layer>
      </Stage>

      {/* EditorAnnotation Toolbar */}
      <MarkupToolbar
        canUndo={canUndo}
        isAnnotating={isAnnotating}
        isDrawing={drawingState.isDrawing}
        markupMode={markupMode}
        selectedId={selectedId}
        onDelete={handleDeleteAnnotation}
        onMarkupModeChange={(mode) => {
          setMarkupMode(mode);
          // Clear selection when switching away from selection mode
          if (mode !== MarkupMode.Selection) {
            setSelectedId(null);
          }
        }}
        onRename={handleRenameClick}
        onToggleAnnotating={() => {
          // Clear selection when exiting annotation mode
          if (isAnnotating) {
            setMarkupMode(MarkupMode.Box);
            setSelectedId(null);
          }
          onToggleAnnotating();
        }}
        onUndo={undo}
      />
    </>
  );

  /**
   * Render stable DOM structure with conditional visibility for smooth transitions
   */
  return (
    <>
      <div className="flex h-full w-full overflow-hidden">
        {/* EditorAnnotation sidebar - conditionally displayed based on annotation mode */}
        <div
          className={cn("flex-shrink-0 transition-all duration-75 ease-in-out", "hidden sm:block")}
          style={{
            width: isAnnotating ? "256px" : "0px",
            overflow: "hidden",
          }}
        >
          <AnnotationList
            annotations={annotations}
            onDelete={handleDeleteAnnotation}
            onSelect={setSelectedId}
            onUpdateLabel={(id, label) => updateAnnotation(id, { label })}
          />
        </div>
        <div className="relative h-full min-w-0 flex-1 bg-black" ref={containerRef}>
          <div className="h-full w-full animate-fade-in">{canvasContent}</div>
        </div>
      </div>

      {/* Rename Sheet - Mobile-optimized label editing */}
      <RenameLabelSheet
        currentLabel={selectedAnnotation?.label || ""}
        isOpen={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        onRename={handleRenameSubmit}
      />
    </>
  );
};

export default AnnotationCanvas;
